/**
 * FigmaImportPanel.js — Design persona's primary New Article entry.
 *
 * Three modes for picking a file:
 *   1. Browse  — list projects in the saved team, then files in the project
 *   2. Recent  — pick from files loaded recently (localStorage)
 *   3. Paste   — paste a Figma file URL
 *
 * All three converge to the same frame-grid + title + Import flow once a
 * file is loaded. On import, POST /api/figma/import creates the article +
 * private review site, navigates to /editor/<article_id>.
 *
 * Team setup is inline (the Browse tab prompts for a team URL on first use).
 * No Settings change required.
 *
 * Layout: source/import column (connection, file picker, frame grid) plus a
 * "Review details" column (project, title, selection count, Import). Styles
 * live in styles/new-article.css (na- prefix).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/new-article.css';
import api from '../api';
import Icon from './Icon';
import { SkeletonRows, Spinner, StatePanel, StatusBadge } from './ui';

const RECENT_KEY = 'affixly_figma_recent_files';
const RECENT_MAX = 10;


function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}


function saveRecent(file_key, name) {
  try {
    const list = loadRecent().filter(r => r.file_key !== file_key);
    list.unshift({ file_key, name: name || '(Untitled)', ts: Date.now() });
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {}
}


export default function FigmaImportPanel({ onCreated, project }) {
  const navigate = useNavigate();
  const [connected, setConnected] = useState(null);
  const [figmaUser, setFigmaUser] = useState(null); // {handle, email} from /figma/status — display only

  // Picker mode + source-specific state
  const [mode, setMode] = useState('browse');
  const [team, setTeam] = useState(null);                  // {team_id, projects: [...]}
  const [teamUrlInput, setTeamUrlInput] = useState('');
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState('');

  const [openProjectId, setOpenProjectId] = useState(null);
  const [projectFiles, setProjectFiles] = useState({});    // {project_id: [...files]}
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState('');

  const [recent, setRecent] = useState([]);

  const [pasteUrl, setPasteUrl] = useState('');
  const [pasteLoading, setPasteLoading] = useState(false);

  // Loaded file + frame picker state (common to all modes)
  const [file, setFile] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [selectedFrameIds, setSelectedFrameIds] = useState(new Set());
  const [title, setTitle] = useState('');

  // Pages the user has hidden from the frame grid (page_id set). Empty = all visible.
  const [hiddenPageIds, setHiddenPageIds] = useState(new Set());

  // Thumbnail URLs fetched lazily per page: { [page_id]: { [node_id]: url } }
  const [pageThumbs, setPageThumbs] = useState({});
  // Pages currently loading thumbnails
  const [thumbLoading, setThumbLoading] = useState(new Set());

  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');

  useEffect(() => {
    api.get('/figma/status')
      .then(r => { setConnected(!!r.data?.connected); setFigmaUser(r.data?.user || null); })
      .catch(() => setConnected(false));
    setRecent(loadRecent());
  }, []);

  // Auto-fetch thumbnails for the visible page(s) when a new file is loaded.
  // Hidden pages fetch lazily on reveal (see togglePage), so a 15-page file
  // doesn't fire 15 thumbnail batches up front.
  useEffect(() => {
    if (!file?.file_key) return;
    const SEP = /^[-–—_=•·\s]+$/;
    (file.pages || [])
      .filter(p => p.frames.length > 0 && !SEP.test(p.page_name) && !hiddenPageIds.has(p.page_id))
      .forEach(page => fetchPageThumbs(page));
  }, [file?.file_key]); // eslint-disable-line

  // Lazy-load team data the first time Browse mode is opened
  useEffect(() => {
    if (mode !== 'browse' || team !== null || !connected) return;
    setTeamLoading(true);
    api.get('/figma/team')
      .then(r => setTeam(r.data || { team_id: null, projects: [] }))
      .catch(e => setTeamError(e.response?.data?.error || 'Could not load team'))
      .finally(() => setTeamLoading(false));
  }, [mode, team, connected]);

  const saveTeamUrl = async () => {
    setTeamError('');
    if (!teamUrlInput.trim()) return;
    setTeamLoading(true);
    try {
      const r = await api.put('/figma/team', { url: teamUrlInput.trim() });
      setTeam(r.data);
      setTeamUrlInput('');
    } catch (e) {
      setTeamError(e.response?.data?.error || 'Invalid Figma team URL');
    } finally {
      setTeamLoading(false);
    }
  };

  const openProject = async (projectId) => {
    setProjectError('');
    setOpenProjectId(projectId);
    if (projectFiles[projectId]) return; // cached
    setProjectLoading(true);
    try {
      const r = await api.get(`/figma/projects/${projectId}/files`);
      setProjectFiles(prev => ({ ...prev, [projectId]: r.data?.files || [] }));
    } catch (e) {
      setProjectError(e.response?.data?.error || 'Could not load files');
    } finally {
      setProjectLoading(false);
    }
  };

  const loadFile = async (urlOrKey) => {
    setFileError('');
    if (!urlOrKey || !urlOrKey.trim()) return;
    setFileLoading(true);
    try {
      const r = await api.get('/figma/file', { params: { url: urlOrKey.trim() } });
      const SEP = /^[-–—_=•·\s]+$/;
      const contentPgs = (r.data?.pages || []).filter(p => p.frames.length > 0 && !SEP.test(p.page_name));
      // Multi-page files (the common case for a real Figma file with Cover,
      // Components, Changelog, etc. pages) start with EVERY page hidden — the
      // designer picks the one page they're reviewing via the chips. A
      // single-page file auto-shows since there's nothing to choose.
      if (contentPgs.length <= 1) {
        setHiddenPageIds(new Set());
        // Pre-select only "Section/Name" frames on the one visible page.
        setSelectedFrameIds(new Set(
          contentPgs.flatMap(p => p.frames.filter(f => f.name.includes('/')).map(f => f.id))
        ));
      } else {
        setHiddenPageIds(new Set(contentPgs.map(p => p.page_id)));
        setSelectedFrameIds(new Set()); // nothing selected until a page is revealed
      }
      setPageThumbs({});
      setThumbLoading(new Set());
      setFile(r.data);
      if (!title.trim()) setTitle(r.data?.name || '');
      saveRecent(r.data?.file_key, r.data?.name);
      setRecent(loadRecent());
    } catch (e) {
      setFileError(e.response?.data?.error || 'Could not load Figma file');
    } finally {
      setFileLoading(false);
      setPasteLoading(false);
    }
  };

  const pasteAndLoad = async () => {
    setPasteLoading(true);
    await loadFile(pasteUrl);
  };

  const toggleFrame = (id) => {
    setSelectedFrameIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleImport = async () => {
    setImportError('');
    if (!file?.file_key || selectedFrameIds.size === 0 || !title.trim()) {
      setImportError('Pick at least one frame and give it a title.');
      return;
    }
    setImporting(true);
    try {
      // Pass frame_names so the backend doesn't re-fetch /v1/files/:key
      // just to look up names — that doubles our Figma rate-limit burn.
      const frameNames = {};
      (file.pages || []).forEach(p => (p.frames || []).forEach(f => {
        frameNames[f.id] = f.name;
      }));
      const r = await api.post('/figma/import', {
        file_key:    file.file_key,
        frame_ids:   Array.from(selectedFrameIds),
        title:       title.trim(),
        frame_names: frameNames,
        // When this panel is mounted inside a project's New Review flow,
        // the backend skips its auto-create-site branch and publishes the
        // resulting review under the project's existing site instead.
        ...(project?.id ? { project_id: project.id } : {}),
      });
      const articleId = r.data?.article_id;
      if (onCreated) onCreated(articleId);
      if (articleId) navigate(`/editor/${articleId}`);
    } catch (e) {
      setImportError(e.response?.data?.error || 'Import failed');
      setImporting(false);
    }
  };

  const resetFile = () => {
    setFile(null);
    setSelectedFrameIds(new Set());
    setHiddenPageIds(new Set());
    setPageThumbs({});
    setThumbLoading(new Set());
    setTitle('');
    setFileError('');
    setImportError('');
  };

  const fetchPageThumbs = async (page, force = false) => {
    if (!file?.file_key || !page?.frames?.length) return;
    const pageId = page.page_id;
    if (!force && pageThumbs[pageId] !== undefined) return;
    setThumbLoading(prev => new Set([...prev, pageId]));
    // Seed an empty map so cards render immediately and thumbs fill in as
    // each chunk resolves (rather than all-or-nothing on one big request).
    setPageThumbs(prev => ({ ...prev, [pageId]: force ? {} : (prev[pageId] || {}) }));

    // Chunk into small batches — Figma renders every requested frame before
    // responding, so one large request for a whole section can exceed the
    // 45s abort and blank every preview. Small chunks resolve fast and the
    // grid fills in progressively.
    const CHUNK = 8;
    const ids = page.frames.map(f => f.id);
    const chunks = [];
    for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));

    // Cap concurrency. The backend web service runs few threads; firing every
    // chunk at once (a 280-frame page = 35 requests) blocks each thread on a
    // synchronous Figma render and starves the whole app. Process a few at a
    // time via a shared cursor so at most CONCURRENCY requests are ever live.
    const CONCURRENCY = 3;
    let cursor = 0;
    const runWorker = async () => {
      while (cursor < chunks.length) {
        const chunk = chunks[cursor++];
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 45000);
        try {
          const r = await api.get('/figma/thumbnails', {
            params: { file_key: file.file_key, node_ids: chunk.join(',') },
            signal: controller.signal,
          });
          // Merge this chunk's URLs into the page map as it arrives.
          setPageThumbs(prev => ({ ...prev, [pageId]: { ...(prev[pageId] || {}), ...(r.data || {}) } }));
        } catch {
          // Leave this chunk's frames absent from the map so "⟳ Retry" appears.
        } finally {
          clearTimeout(timeout);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, runWorker));

    setThumbLoading(prev => { const s = new Set(prev); s.delete(pageId); return s; });
  };

  const togglePage = (pageId) => {
    const page = (file?.pages || []).find(p => p.page_id === pageId);
    const wasHidden = hiddenPageIds.has(pageId);
    // Move side effect (API call) outside the state setter — React 18 treats
    // state setter callbacks as pure and can suppress effects called from them.
    if (wasHidden) {
      setHiddenPageIds(prev => { const s = new Set(prev); s.delete(pageId); return s; });
      fetchPageThumbs(page);
      // Auto-select only "Section/Name" frames on reveal, matching load-time
      // behavior — utility/icon frames stay unselected.
      setSelectedFrameIds(sel => {
        const s = new Set(sel);
        (page?.frames || []).filter(f => f.name.includes('/')).forEach(f => s.add(f.id));
        return s;
      });
    } else {
      setHiddenPageIds(prev => { const s = new Set(prev); s.add(pageId); return s; });
      if (page) {
        setSelectedFrameIds(sel => {
          const s = new Set(sel);
          (page.frames || []).forEach(f => s.delete(f.id));
          return s;
        });
      }
    }
  };

  // Pages that have at least one frame and aren't Figma separator pages
  // (designers use all-dash/underscore names like "----------" as visual dividers).
  const contentPages = useMemo(() => {
    const SEP = /^[-–—_=•·\s]+$/;
    return (file?.pages || []).filter(p => p.frames.length > 0 && !SEP.test(p.page_name));
  }, [file]);

  // ── Render ───────────────────────────────────────────────────────────
  // Two-column above 1100px: source/import (2/3) + review context (1/3);
  // stacked (source first) below. Auth/import errors sit beside the control
  // that produced them; frame selection is never cleared by an error.

  const totalFrames = contentPages.reduce((n, p) => n + p.frames.length, 0);
  const selectedCount = selectedFrameIds.size;
  const importBlockedReason = !file
    ? ''
    : selectedCount === 0
      ? 'Select at least one frame to import.'
      : !title.trim() ? 'Give the review a title.' : '';

  const MODES = [
    { id: 'browse', label: 'Browse team' },
    { id: 'recent', label: 'Recent', count: recent.length || null },
    { id: 'paste',  label: 'Paste URL' },
  ];
  const onModeKey = (e, i) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const n = MODES.length;
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
    setMode(MODES[next].id);
    document.getElementById(`na-fmode-${MODES[next].id}`)?.focus();
  };

  const projectNote = project ? (
    <div className="na-context-project na-text">
      Adding to <strong>{project.title}</strong>
      {project.slug && (
        <div className="meta na-mono" style={{ marginTop: 2 }}>{project.slug}.sites.affixly.ai</div>
      )}
    </div>
  ) : (
    <p className="na-text">A private review site is created for this review when you import.</p>
  );

  // Connection card — the "source control" for this variant.
  const connectionCard = (
    <div className="na-conn">
      <span className="na-conn-mark" aria-hidden="true"><Icon name="design" size={22} /></span>
      <div className="na-conn-text">
        <div className="na-conn-name">
          Figma
          {connected === null ? (
            <StatusBadge tone="neutral" icon="clock">Checking connection</StatusBadge>
          ) : connected ? (
            <StatusBadge tone="success">Connected</StatusBadge>
          ) : (
            <StatusBadge tone="warning" icon="alert">Not connected</StatusBadge>
          )}
        </div>
        <div className="meta">
          {connected === null
            ? 'Checking your Figma connection…'
            : connected
              ? (figmaUser?.handle ? `Signed in as ${figmaUser.handle}` : figmaUser?.email ? `Signed in as ${figmaUser.email}` : 'Ready to import frames')
              : 'Affixly only requests read access to file metadata and content.'}
        </div>
      </div>
      {file && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={resetFile}>
          <Icon name="chevronLeft" /> Change file
        </button>
      )}
    </div>
  );

  let sourceBody;
  if (connected === null) {
    sourceBody = <SkeletonRows rows={3} height={44} label="Checking Figma connection" />;
  } else if (!connected) {
    sourceBody = (
      <div className="na-stack">
        <p className="na-text">Connect Figma to import designs, then come back here to pick frames.</p>
        <div>
          <Link className="btn btn-primary" to="/settings#figma">
            <Icon name="plug" /> Connect Figma in Settings
          </Link>
        </div>
      </div>
    );
  } else if (file) {
    // Once a file is loaded, all three modes show the same frame picker.
    sourceBody = (
      <div className="na-stack">
        <div className="na-row na-row--between">
          <div className="na-grow">
            <h2 className="na-subhead" style={{ overflowWrap: 'anywhere' }}>{file.name || 'Untitled'}</h2>
            <div className="meta">
              {totalFrames} frame{totalFrames === 1 ? '' : 's'} across {contentPages.length} page{contentPages.length === 1 ? '' : 's'}
            </div>
          </div>
          <div className="na-row" role="group" aria-label="Frame selection">
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => setSelectedFrameIds(new Set(contentPages.flatMap(p => p.frames.map(f => f.id))))}>
              Select all
            </button>
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={() => setSelectedFrameIds(new Set())}>
              Select none
            </button>
          </div>
        </div>

        {contentPages.length === 0 && (
          <StatePanel compact icon="design" title="No frames in this file">
            This file has no top-level frames. Choose a different file.
          </StatePanel>
        )}

        {/* ── Page filter chips ── */}
        {contentPages.length > 1 && (
          <div>
            <div className="label" id="na-pages-label">Pages to review</div>
            <div className="na-chips" role="group" aria-labelledby="na-pages-label">
              {contentPages.map(page => {
                const visible = !hiddenPageIds.has(page.page_id);
                return (
                  <button
                    key={page.page_id}
                    type="button"
                    className="na-chip"
                    aria-pressed={visible}
                    onClick={() => togglePage(page.page_id)}
                  >
                    {visible && <Icon name="check" size={16} />}
                    <span className="na-chip-name">{page.page_name}</span>
                    <span className="na-chip-count">
                      {page.frames.length}<span className="visually-hidden"> frames</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {contentPages.length > 1 && contentPages.every(p => hiddenPageIds.has(p.page_id)) && (
          <StatePanel compact icon="design" title="Pick a page">
            Select a page above to load its frames.
          </StatePanel>
        )}

        {contentPages.filter(p => !hiddenPageIds.has(p.page_id)).map(page => {
          const isLoadingThumbs = thumbLoading.has(page.page_id);
          const thumbMap = pageThumbs[page.page_id];
          // Show retry when: loaded but empty (Figma 400 / file not ready)
          // OR loaded but every URL came back null (file still rendering).
          const allNull = thumbMap !== undefined && (
            Object.keys(thumbMap).length === 0 ||
            Object.values(thumbMap).every(v => !v)
          );
          return (
            <section key={page.page_id} className="na-page-block" aria-label={`${page.page_name} frames`}>
              <div className="na-page-head">
                <h3 className="na-subhead" style={{ fontSize: 15, overflowWrap: 'anywhere' }}>{page.page_name}</h3>
                <span className="meta">{page.frames.length} frame{page.frames.length === 1 ? '' : 's'}</span>
                {isLoadingThumbs && <span className="na-busy" role="status"><Spinner size={14} /> Loading previews…</span>}
                {!isLoadingThumbs && allNull && (
                  <span className="na-row" style={{ gap: 8 }}>
                    <span className="meta" style={{ color: 'var(--gp-amber)' }}>Previews didn’t load.</span>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => fetchPageThumbs(page, true)}>
                      <Icon name="refresh" /> Retry previews
                    </button>
                  </span>
                )}
              </div>
              <FrameGrid
                frames={page.frames}
                thumbMap={thumbMap}
                isLoadingThumbs={isLoadingThumbs}
                selectedFrameIds={selectedFrameIds}
                onToggle={toggleFrame}
              />
            </section>
          );
        })}
      </div>
    );
  } else {
    // Source-picker tabs (Browse / Recent / Paste URL)
    sourceBody = (
      <div>
        <div className="tablist" role="tablist" aria-label="Choose a Figma file">
          {MODES.map((t, i) => (
            <button
              key={t.id}
              id={`na-fmode-${t.id}`}
              type="button"
              role="tab"
              aria-selected={mode === t.id}
              aria-controls="na-fmode-panel"
              tabIndex={mode === t.id ? 0 : -1}
              onClick={() => setMode(t.id)}
              onKeyDown={e => onModeKey(e, i)}
            >
              {t.label}
              {t.count ? <span className="tab-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        <div id="na-fmode-panel" role="tabpanel" aria-labelledby={`na-fmode-${mode}`} className="na-mode-panel na-stack">
          {mode === 'browse' && (
            <BrowseMode
              team={team} teamLoading={teamLoading} teamError={teamError}
              teamUrlInput={teamUrlInput} setTeamUrlInput={setTeamUrlInput}
              onSaveTeamUrl={saveTeamUrl}
              openProjectId={openProjectId} openProject={openProject}
              projectFiles={projectFiles} projectLoading={projectLoading} projectError={projectError}
              onPickFile={loadFile}
              fileLoading={fileLoading}
            />
          )}

          {mode === 'recent' && (
            <RecentMode
              recent={recent}
              onPickFile={loadFile}
              fileLoading={fileLoading}
              onClear={() => { localStorage.removeItem(RECENT_KEY); setRecent([]); }}
            />
          )}

          {mode === 'paste' && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="label" htmlFor="na-figma-url">Figma URL</label>
              <div className="na-input-row">
                <input
                  id="na-figma-url"
                  className="input na-mono"
                  type="url"
                  inputMode="url"
                  value={pasteUrl}
                  onChange={e => setPasteUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && pasteUrl.trim() && !pasteLoading) pasteAndLoad(); }}
                  placeholder="https://www.figma.com/design/…"
                  aria-invalid={fileError ? true : undefined}
                  aria-describedby={fileError ? 'na-figma-file-error' : 'na-figma-url-hint'}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={pasteAndLoad}
                  disabled={pasteLoading || !pasteUrl.trim()}
                  aria-busy={pasteLoading || undefined}
                >
                  {pasteLoading ? <><Spinner size={16} /> Loading frames…</> : 'Load frames'}
                </button>
              </div>
              <div className="field-hint" id="na-figma-url-hint">A Figma Design file link (figma.com/design/… or figma.com/file/…).</div>
            </div>
          )}

          {fileLoading && mode !== 'paste' && (
            <div className="na-busy" role="status"><Spinner size={16} /> Loading Figma file…</div>
          )}

          {fileError && (
            <div className="inline-msg is-error" role="alert" id="na-figma-file-error">
              <Icon name="alert" />
              <div className="na-msg-body">{fileError}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="na-review-grid">
      <section className="panel na-stack" aria-label="Figma source" style={{ gap: 'var(--sp-5)' }}>
        {connectionCard}
        <hr className="na-hr" />
        {sourceBody}
      </section>

      <aside className="panel na-context" aria-label="Review details">
        <h2 className="section-title">Review details</h2>
        {projectNote}

        {file ? (
          <>
            <div className="form-group">
              <label className="label" htmlFor="na-review-title">Review title</label>
              <input
                id="na-review-title"
                className="input"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Homepage redesign"
                aria-required="true"
              />
            </div>
            <p className="na-text" aria-live="polite">
              <strong>{selectedCount}</strong> of {totalFrames} frame{totalFrames === 1 ? '' : 's'} selected
            </p>
            <div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleImport}
                disabled={importing || selectedCount === 0 || !title.trim()}
                aria-busy={importing || undefined}
                aria-describedby={importBlockedReason ? 'na-import-reason' : undefined}
              >
                {importing
                  ? <><Spinner size={16} /> Importing {selectedCount} frame{selectedCount === 1 ? '' : 's'}…</>
                  : `Import ${selectedCount} frame${selectedCount === 1 ? '' : 's'}`}
              </button>
              {importBlockedReason && !importing && (
                <div className="disabled-reason" id="na-import-reason">{importBlockedReason}</div>
              )}
            </div>
            {importError && (
              <div className="inline-msg is-error" role="alert">
                <Icon name="alert" />
                <div className="na-msg-body">
                  <p><strong>Import failed.</strong> {importError}</p>
                  <p>Your frame selection and title are kept — try again.</p>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="field-hint" style={{ marginTop: 0 }}>
            Load a Figma file to choose frames. Only frames from the loaded file can be imported.
          </p>
        )}
      </aside>
    </div>
  );
}


function BrowseMode({
  team, teamLoading, teamError,
  teamUrlInput, setTeamUrlInput, onSaveTeamUrl,
  openProjectId, openProject,
  projectFiles, projectLoading, projectError,
  onPickFile, fileLoading,
}) {
  if (teamLoading && !team) {
    return <SkeletonRows rows={3} height={44} label="Loading Figma team" />;
  }

  // No team configured — show the team-URL setup
  if (!team || !team.team_id) {
    return (
      <div className="na-stack">
        <div>
          <h2 className="na-subhead">Connect a Figma team to browse</h2>
          <p className="na-text" style={{ marginTop: 4 }}>
            Figma’s API only allows browsing inside teams you’ve added. Paste a team URL once
            (Figma → left rail → right-click your team → Copy link) and we’ll list projects and files.
          </p>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="label" htmlFor="na-team-url">Figma team URL</label>
          <div className="na-input-row">
            <input
              id="na-team-url"
              className="input na-mono"
              type="url"
              inputMode="url"
              value={teamUrlInput}
              onChange={e => setTeamUrlInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && teamUrlInput.trim() && !teamLoading) onSaveTeamUrl(); }}
              placeholder="https://www.figma.com/files/team/…"
              aria-invalid={teamError ? true : undefined}
              aria-describedby={teamError ? 'na-team-error' : undefined}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSaveTeamUrl}
              disabled={teamLoading || !teamUrlInput.trim()}
              aria-busy={teamLoading || undefined}
            >
              {teamLoading ? <><Spinner size={16} /> Saving…</> : 'Save team'}
            </button>
          </div>
          {teamError && (
            <div className="field-error" id="na-team-error" role="alert"><Icon name="alert" size={16} /> {teamError}</div>
          )}
        </div>
      </div>
    );
  }

  // Team is set — show projects list
  const projects = team.projects || [];
  return (
    <div className="na-stack">
      <div className="na-row na-row--between">
        <span className="meta">{projects.length} project{projects.length === 1 ? '' : 's'} in team</span>
        <a className="link" href={`https://www.figma.com/files/team/${team.team_id}`} target="_blank" rel="noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
          Open team in Figma <Icon name="external" size={16} /><span className="visually-hidden"> (opens in a new tab)</span>
        </a>
      </div>

      {team.error && (
        <div className="inline-msg is-warning" role="alert">
          <Icon name="warning" />
          <div className="na-msg-body">Couldn’t list this team’s projects: {team.error}</div>
        </div>
      )}

      {projects.length === 0 && !team.error && (
        <StatePanel compact icon="design" title="No projects found">
          This team has no projects, or your Figma access doesn’t include them.
        </StatePanel>
      )}

      <ul className="na-projects">
        {projects.map(p => {
          const isOpen = openProjectId === p.id;
          const files = projectFiles[p.id] || [];
          const bodyId = `na-proj-${p.id}`;
          return (
            <li key={p.id} className="na-project">
              <button
                type="button"
                className="na-project-toggle"
                aria-expanded={isOpen}
                aria-controls={bodyId}
                onClick={() => openProject(p.id)}
              >
                <Icon name="chevronRight" size={18} />
                <span>{p.name}</span>
              </button>
              {isOpen && (
                <div className="na-project-body" id={bodyId}>
                  {projectLoading && files.length === 0 ? (
                    <SkeletonRows rows={2} height={44} label="Loading files" />
                  ) : files.length === 0 ? (
                    <p className="na-text">No files in this project.</p>
                  ) : (
                    <div className="na-file-grid">
                      {files.map(f => (
                        <button key={f.key} type="button" className="na-file" onClick={() => onPickFile(f.key)}
                          disabled={fileLoading}>
                          {f.thumbnail_url ? (
                            <img className="na-file-thumb" src={f.thumbnail_url} alt="" loading="lazy" />
                          ) : (
                            <span className="na-file-thumb na-file-thumb--empty">No preview</span>
                          )}
                          <span className="na-file-name" title={f.name}>{f.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {projectError && (
        <div className="inline-msg is-error" role="alert">
          <Icon name="alert" />
          <div className="na-msg-body">{projectError}</div>
        </div>
      )}
    </div>
  );
}


function FrameThumb({ f, thumbMap, isLoadingThumbs, selected, onToggle }) {
  const thumbUrl = thumbMap?.[f.id];
  return (
    <label className={`na-frame ${selected ? 'is-selected' : ''}`}>
      <input type="checkbox" checked={selected} onChange={() => onToggle(f.id)} />
      {thumbUrl ? (
        <img className="na-frame-thumb" src={thumbUrl} alt="" loading="lazy" />
      ) : (
        <span className="na-frame-thumb na-frame-thumb--empty">
          {isLoadingThumbs ? <Spinner size={16} /> : 'No preview'}
        </span>
      )}
      <span className="na-frame-name">{f.name}</span>
    </label>
  );
}

// Groups frames by their "Section/Number" prefix. Frames without "/" are
// treated as ungrouped (section covers or standalone) and rendered flat first.
function FrameGrid({ frames, thumbMap, isLoadingThumbs, selectedFrameIds, onToggle }) {
  const ungrouped = frames.filter(f => !f.name.includes('/'));
  const grouped = {};
  frames.filter(f => f.name.includes('/')).forEach(f => {
    const section = f.name.split('/')[0].trim();
    (grouped[section] = grouped[section] || []).push(f);
  });

  return (
    <div className="na-stack" style={{ gap: 'var(--sp-3)' }}>
      {ungrouped.length > 0 && (
        <div className="na-frame-grid">
          {ungrouped.map(f => (
            <FrameThumb key={f.id} f={f} thumbMap={thumbMap} isLoadingThumbs={isLoadingThumbs}
              selected={selectedFrameIds.has(f.id)} onToggle={onToggle} />
          ))}
        </div>
      )}
      {Object.entries(grouped).map(([section, sectionFrames]) => (
        <div key={section} role="group" aria-label={section}>
          <div className="na-section-label" aria-hidden="true">{section}</div>
          <div className="na-frame-grid" style={{ marginTop: 'var(--sp-2)' }}>
            {sectionFrames.map(f => (
              <FrameThumb key={f.id} f={f} thumbMap={thumbMap} isLoadingThumbs={isLoadingThumbs}
                selected={selectedFrameIds.has(f.id)} onToggle={onToggle} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


function RecentMode({ recent, onPickFile, fileLoading, onClear }) {
  if (!recent.length) {
    return (
      <StatePanel compact icon="clock" title="No recent files yet">
        Load a file from Browse team or Paste URL and it’ll show up here next time.
      </StatePanel>
    );
  }
  return (
    <div className="na-stack">
      <div className="na-row na-row--between">
        <span className="meta">Recently loaded on this device</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClear}>
          Clear list
        </button>
      </div>
      <ul className="na-recent">
        {recent.map(r => (
          <li key={r.file_key}>
            <button type="button" className="na-recent-btn" onClick={() => onPickFile(r.file_key)} disabled={fileLoading}>
              <span className="na-recent-name" title={r.name}>{r.name}</span>
              <span className="na-recent-date">{new Date(r.ts).toLocaleDateString()}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
